import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Copy, Check, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

// SVG sanitizer config — Mermaid generates SVG with inline event handlers
// that we MUST strip. ADD_TAGS / ADD_ATTR allow only the markup primitives
// Mermaid relies on; everything else (script, on*, foreignObject, etc.) is
// scrubbed by DOMPurify's default disallow list.
const _MERMAID_SVG_PURIFY_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  ADD_TAGS: ['foreignObject'],
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['script'],
  FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout',
                'onmousedown', 'onmouseup', 'onmousemove', 'onfocus', 'onblur',
                'onkeydown', 'onkeyup', 'onkeypress', 'href'],
};

function sanitizeSvg(svg) {
  if (!svg) return '';
  try {
    return DOMPurify.sanitize(svg, _MERMAID_SVG_PURIFY_CONFIG);
  } catch (e) {
    return '';
  }
}

// ==================== INTERACTIVE TABLE ====================
const InteractiveTable = function({ headers, rows, tableIndex }) {
  const [sortCol, setSortCol] = useState(-1);
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSort = function(colIdx) {
    if (sortCol === colIdx) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(colIdx);
      setSortDir('asc');
    }
  };

  const handleFilter = function(colIdx, value) {
    const newFilters = Object.assign({}, filters);
    if (value) {
      newFilters[colIdx] = value.toLowerCase();
    } else {
      delete newFilters[colIdx];
    }
    setFilters(newFilters);
  };

  const processedRows = useMemo(function() {
    var result = rows.slice();

    // Apply filters
    var filterKeys = Object.keys(filters);
    for (var fi = 0; fi < filterKeys.length; fi++) {
      var colIdx = parseInt(filterKeys[fi]);
      var filterVal = filters[filterKeys[fi]];
      result = result.filter(function(row) {
        return row[colIdx] && row[colIdx].toLowerCase().indexOf(filterVal) !== -1;
      });
    }

    // Apply sort
    if (sortCol >= 0) {
      result.sort(function(a, b) {
        var valA = (a[sortCol] || '').toLowerCase();
        var valB = (b[sortCol] || '').toLowerCase();
        // Try numeric sort
        var numA = parseFloat(valA);
        var numB = parseFloat(valB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortDir === 'asc' ? numA - numB : numB - numA;
        }
        if (valA < valB) return sortDir === 'asc' ? -1 : 1;
        if (valA > valB) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [rows, sortCol, sortDir, filters]);

  const copyTable = function() {
    var text = headers.join('\t') + '\n';
    for (var ri = 0; ri < processedRows.length; ri++) {
      text += processedRows[ri].join('\t') + '\n';
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Table copied to clipboard');
    setTimeout(function() { setCopied(false); }, 2000);
  };

  // Build header cells
  var headerCells = [];
  for (var hi = 0; hi < headers.length; hi++) {
    var SortIcon = sortCol === hi ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    headerCells.push(
      <th
        key={hi}
        onClick={((idx) => () => handleSort(idx))(hi)}
        className="px-3 py-2.5 text-left text-xs font-semibold text-primary-foreground cursor-pointer hover:bg-primary/80 transition-colors select-none whitespace-nowrap"
      >
        <span className="flex items-center gap-1.5">
          {headers[hi]}
          <SortIcon className="h-3 w-3 opacity-70" />
        </span>
      </th>
    );
  }

  // Build filter row
  var filterCells = [];
  for (var fci = 0; fci < headers.length; fci++) {
    filterCells.push(
      <th key={fci} className="px-2 py-1.5">
        <input
          type="text"
          placeholder="Filter..."
          value={filters[fci] || ''}
          onChange={((idx) => (e) => handleFilter(idx, e.target.value))(fci)}
          className="w-full px-2 py-1 text-xs rounded bg-background border border-border/50 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
          data-testid={'table-filter-' + tableIndex + '-' + fci}
        />
      </th>
    );
  }

  // Build body rows
  var bodyRows = [];
  for (var ri = 0; ri < processedRows.length; ri++) {
    var cells = [];
    for (var ci = 0; ci < headers.length; ci++) {
      cells.push(
        <td key={ci} className="px-3 py-2 text-sm border-t border-border/30 whitespace-nowrap">
          {processedRows[ri][ci] || ''}
        </td>
      );
    }
    bodyRows.push(
      <tr key={ri} className={(ri % 2 === 0 ? 'bg-transparent' : 'bg-muted/30') + ' hover:bg-primary/5 transition-colors'}>
        {cells}
      </tr>
    );
  }

  return (
    <div className="my-3 rounded-xl border border-border/50 overflow-hidden" data-testid={'interactive-table-' + tableIndex}>
      {/* Table toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border/30">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{processedRows.length} of {rows.length} rows</span>
          {Object.keys(filters).length > 0 && (
            <button
              onClick={function() { setFilters({}); }}
              className="text-xs text-primary hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={function() { setShowFilters(!showFilters); }}
            className={'p-1.5 rounded-md text-xs transition-colors ' + (showFilters ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary')}
            data-testid={'toggle-filters-' + tableIndex}
          >
            <Search className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={copyTable}
            className="p-1.5 rounded-md text-xs text-muted-foreground hover:bg-secondary transition-colors"
            data-testid={'copy-table-' + tableIndex}
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-primary">
              {headerCells}
            </tr>
            {showFilters && (
              <tr className="bg-muted/50">
                {filterCells}
              </tr>
            )}
          </thead>
          <tbody>
            {bodyRows}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== MERMAID DIAGRAM ====================
const MermaidDiagram = function({ code, diagramIndex }) {
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(function() {
    var cancelled = false;

    async function renderDiagram() {
      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
          securityLevel: 'loose',
          flowchart: { useMaxWidth: true, htmlLabels: true },
          sequence: { useMaxWidth: true },
        });

        const diagramId = 'mermaid-' + diagramIndex + '-' + Date.now();
        const { svg } = await mermaid.render(diagramId, code.trim());
        if (!cancelled) {
          setSvgContent(svg);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to render diagram');
          setSvgContent('');
        }
      }
    }

    renderDiagram();
    return function() { cancelled = true; };
  }, [code, diagramIndex]);

  const zoomIn = function() { setScale(Math.min(scale + 0.25, 3)); };
  const zoomOut = function() { setScale(Math.max(scale - 0.25, 0.5)); };

  if (error) {
    return (
      <div className="my-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid={'mermaid-error-' + diagramIndex}>
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 font-medium">Diagram rendering failed</p>
        <pre className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg overflow-x-auto font-mono">{code}</pre>
      </div>
    );
  }

  if (!svgContent) {
    return (
      <div className="my-3 rounded-xl border border-border/50 p-6 flex items-center justify-center" data-testid={'mermaid-loading-' + diagramIndex}>
        <div className="animate-pulse text-xs text-muted-foreground">Rendering diagram...</div>
      </div>
    );
  }

  // Defence-in-depth: DOMPurify-sanitize the Mermaid-rendered SVG before
  // we feed it to dangerouslySetInnerHTML. Strips inline event handlers,
  // <script>, hyperlinks, and anything else outside the allowlist.
  const sanitizedSvg = sanitizeSvg(svgContent);

  return (
    <div className={'my-3 rounded-xl border border-border/50 overflow-hidden ' + (expanded ? 'fixed inset-4 z-50 bg-background shadow-2xl' : '')} data-testid={'mermaid-diagram-' + diagramIndex}>
      {/* Diagram toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border/30">
        <span className="text-xs text-muted-foreground font-medium">Architecture Diagram</span>
        <div className="flex items-center gap-1">
          <button onClick={zoomOut} className="p-1.5 rounded-md text-muted-foreground hover:bg-secondary transition-colors" data-testid={'mermaid-zoom-out-' + diagramIndex}>
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs text-muted-foreground tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="p-1.5 rounded-md text-muted-foreground hover:bg-secondary transition-colors" data-testid={'mermaid-zoom-in-' + diagramIndex}>
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <button onClick={function() { setExpanded(!expanded); }} className="p-1.5 rounded-md text-muted-foreground hover:bg-secondary transition-colors ml-1" data-testid={'mermaid-expand-' + diagramIndex}>
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Diagram content */}
      <div className={'overflow-auto p-4 bg-background ' + (expanded ? 'flex-1' : 'max-h-[400px]')} ref={containerRef}>
        <div
          style={{ transform: 'scale(' + scale + ')', transformOrigin: 'top left', transition: 'transform 0.2s ease' }}
          className="inline-block"
          dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
        />
      </div>

      {/* Fullscreen backdrop */}
      {expanded && (
        <div className="fixed inset-0 bg-black/50 -z-10" onClick={function() { setExpanded(false); }} />
      )}
    </div>
  );
};

// ==================== RICH CONTENT RENDERER ====================
function parseContentBlocks(content) {
  var blocks = [];
  var lines = content.split('\n');
  var i = 0;

  while (i < lines.length) {
    var line = lines[i];
    var trimmed = line.trim();

    // Detect mermaid code blocks
    if (trimmed === '```mermaid' || trimmed === '``` mermaid') {
      var mermaidCode = [];
      i++;
      while (i < lines.length && lines[i].trim() !== '```') {
        mermaidCode.push(lines[i]);
        i++;
      }
      if (mermaidCode.length > 0) {
        blocks.push({ type: 'mermaid', content: mermaidCode.join('\n') });
      }
      i++; // skip closing ```
      continue;
    }

    // Detect other code blocks (pass through as text)
    if (trimmed.startsWith('```')) {
      var codeLines = [line];
      var lang = trimmed.slice(3).trim();
      i++;
      while (i < lines.length && lines[i].trim() !== '```') {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) codeLines.push(lines[i]);
      blocks.push({ type: 'code', content: codeLines.join('\n'), lang: lang });
      i++;
      continue;
    }

    // Detect markdown tables
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      var tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2) {
        // Parse table
        var headerLine = tableLines[0];
        var headerCells = headerLine.split('|').slice(1, -1);
        var headers = [];
        for (var h = 0; h < headerCells.length; h++) {
          headers.push(headerCells[h].trim());
        }

        // Check for separator row
        var dataStart = 1;
        if (tableLines.length > 1 && /^[\s\-:|]+$/.test(tableLines[1].replace(/\|/g, ''))) {
          dataStart = 2;
        }

        var rows = [];
        for (var r = dataStart; r < tableLines.length; r++) {
          var rowCells = tableLines[r].split('|').slice(1, -1);
          var row = [];
          for (var rc = 0; rc < rowCells.length; rc++) {
            row.push(rowCells[rc].trim());
          }
          rows.push(row);
        }

        if (headers.length > 0 && rows.length > 0) {
          blocks.push({ type: 'table', headers: headers, rows: rows });
          continue;
        }
      }

      // If not a valid table, treat as text
      blocks.push({ type: 'text', content: tableLines.join('\n') });
      continue;
    }

    // Regular text line - accumulate
    var textLines = [line];
    i++;
    while (i < lines.length) {
      var nextTrimmed = lines[i].trim();
      if (nextTrimmed === '```mermaid' || nextTrimmed === '``` mermaid' || nextTrimmed.startsWith('```') ||
          (nextTrimmed.startsWith('|') && nextTrimmed.endsWith('|'))) {
        break;
      }
      textLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'text', content: textLines.join('\n') });
  }

  return blocks;
}

const RichContent = function({ content }) {
  var blocks = useMemo(function() {
    return parseContentBlocks(content);
  }, [content]);

  var tableCount = 0;
  var mermaidCount = 0;

  var elements = [];
  for (var bi = 0; bi < blocks.length; bi++) {
    var block = blocks[bi];

    if (block.type === 'table') {
      elements.push(
        <InteractiveTable
          key={'table-' + tableCount}
          headers={block.headers}
          rows={block.rows}
          tableIndex={tableCount}
        />
      );
      tableCount++;
    } else if (block.type === 'mermaid') {
      elements.push(
        <MermaidDiagram
          key={'mermaid-' + mermaidCount}
          code={block.content}
          diagramIndex={mermaidCount}
        />
      );
      mermaidCount++;
    } else if (block.type === 'code') {
      elements.push(
        <pre key={'code-' + bi} className="my-2 p-3 rounded-lg bg-muted/50 border border-border/30 overflow-x-auto text-xs font-mono">
          {block.content}
        </pre>
      );
    } else {
      // Regular text
      if (block.content.trim()) {
        elements.push(
          <p key={'text-' + bi} className="whitespace-pre-wrap leading-relaxed">{block.content}</p>
        );
      }
    }
  }

  return <div data-testid="rich-content">{elements}</div>;
};

export { RichContent, InteractiveTable, MermaidDiagram };
export default RichContent;
